import { useCallback, useEffect, useState } from "react";
import { isNativeAndroidApp, isNativeiOSApp } from "../NativeAppBrdige";
import { useHub } from "../core/Global";

declare global {
    interface Window {
        // Injected by the native iOS host once the merchant has accepted Apple's
        // Tap to Pay Terms & Conditions and finished reader setup.
        __RINVOX_TAPTOPAY_ENABLED__?: boolean
        // Injected by the native iOS host once it has read the account's gateway
        // config from the server (i.e. the account has payment keys). Single
        // source of truth for whether any Tap to Pay UI may appear.
        __RINVOX_TAPTOPAY_GATEWAY__?: boolean
    }
}

// The awareness communication has been shown to this user at least once
// (Apple requirement 3.3).
const SEEN_KEY = "rinvox-taptopay-awareness-seen";

/**
 * Tap to Pay on iPhone state + actions, shared by the awareness banner, the
 * account menu entry and the invoice checkout button.
 *
 * Enablement (accepting Apple's Terms & Conditions + reader setup) happens
 * natively via ChipDNA / Apple; the web layer only triggers it through the
 * `rinvox://taptopaysetup` URL scheme and reflects the status the native host
 * reports back.
 */
// Phone-width breakpoint — matches useScreen().isMobile in core/Global.
const PHONE_MAX_WIDTH = 600;

export function useTapToPay() {

    const hub = useHub();

    // Tap to Pay surfaces must only appear on a phone (not iPad/desktop layout).
    // Tracked reactively so rotation / resize is respected.
    const [isPhone, isPhoneSet] = useState<boolean>(() => (window.innerWidth || 0) < PHONE_MAX_WIDTH);
    useEffect(() => {
        const onResize = () => isPhoneSet((window.innerWidth || 0) < PHONE_MAX_WIDTH);
        window.addEventListener("resize", onResize);
        onResize();
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // Only a native app (iOS or Android), in phone form factor, can run Tap to Pay.
    // iOS drives enablement through Apple's T&C flow; Android reports readiness
    // directly through the injected flags/events (no separate setup step).
    const eligible = (isNativeiOSApp() || isNativeAndroidApp()) && isPhone;

    const [enabled, enabledSet] = useState<boolean>(() => window.__RINVOX_TAPTOPAY_ENABLED__ === true);
    const [gatewayConfigured, gatewayConfiguredSet] = useState<boolean>(() => window.__RINVOX_TAPTOPAY_GATEWAY__ === true);
    // Merchant's Tap to Pay switch, read straight from the server so the web
    // layer honours it immediately (default off until the server confirms it's
    // on — no flash before the fetch resolves).
    const [merchantEnabled, merchantEnabledSet] = useState<boolean>(false);
    const [seen, seenSet] = useState<boolean>(() => {
        try { return localStorage.getItem(SEEN_KEY) === "1"; } catch { return false; }
    });

    // The native host reports enablement changes (T&C accepted, setup done/undone).
    useEffect(() => {
        const onStatus = (e: any) => enabledSet(e?.detail?.enabled === true);
        window.addEventListener("rinvox-taptopay-status-changed", onStatus);
        return () => window.removeEventListener("rinvox-taptopay-status-changed", onStatus);
    }, []);

    // The native host reports whether the account has gateway keys configured.
    // Tap to Pay UI is only offered to merchants whose StellarPay integration is
    // set up on the server.
    useEffect(() => {
        const onGateway = (e: any) => gatewayConfiguredSet(e?.detail?.configured === true);
        window.addEventListener("rinvox-taptopay-gateway-changed", onGateway);
        // Re-sync in case the flag was injected before this hook mounted.
        gatewayConfiguredSet(window.__RINVOX_TAPTOPAY_GATEWAY__ === true);
        return () => window.removeEventListener("rinvox-taptopay-gateway-changed", onGateway);
    }, []);

    // Read the merchant's Tap to Pay switch from the server. This is the
    // authoritative gate for the feature being turned on; the native gateway
    // flag only reflects whether keys exist.
    useEffect(() => {
        hub.Get("/accounts/profile/paymentkeys/taptopay/status/", res => {
            merchantEnabledSet(res?.data?.enabled === true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const markSeen = useCallback(() => {
        try { localStorage.setItem(SEEN_KEY, "1"); } catch { }
        seenSet(true);
    }, []);

    // Kicks off Apple's Tap to Pay Terms & Conditions + reader setup, presented
    // natively. The user must scroll through and accept the T&Cs there.
    const startSetup = useCallback(() => {
        markSeen();
        window.location.href = "rinvox://taptopaysetup";
    }, [markSeen]);

    // Opens Apple's built-in merchant education screens (how to accept
    // contactless cards, Apple Pay and other wallets). Kept reachable from the
    // account menu once setup is done — Apple requirement 4.2.
    const openEducation = useCallback(() => {
        window.location.href = "rinvox://taptopayeducation";
    }, []);

    // Tap to Pay is available only when the account has gateway keys AND the
    // owner has switched the feature on. Every surface gates on this, so turning
    // the switch off hides Tap to Pay everywhere in the app.
    const available = gatewayConfigured && merchantEnabled;

    // Highly-visible awareness is shown to every eligible merchant (native iOS +
    // Tap to Pay available) who hasn't enabled Tap to Pay yet
    // (Apple requirements 3.1 & 3.3).
    const shouldShowBanner = eligible && available && !enabled;

    return {
        // gatewayConfigured reflects full availability (keys + switch on) for
        // consumers that gate their own Tap to Pay surfaces on it.
        eligible, enabled, gatewayConfigured: available, merchantEnabled, seen,
        shouldShowBanner,
        markSeen, startSetup, openEducation,
    };
}
